import { useMemo } from 'react'
import type { TrainOption } from '~/app/components/Cart/CartItem/DomesticTrainItem'
import { DomesticBusCartItemOption } from '~/__generated__/__types__'
import { busUnavailableReasonText } from '~/app/utils/filters'
import { isEmpty, isNil } from 'ramda'
import { getTrainImage } from '~/app/components/order-converter/converter/TrainOrderConverter'
import { toCoin, toPoint } from '~/app/utils/krNumberToString'

export function useSeatMemo(option: TrainOption) {
  return useMemo(() => {
    if (!option) {
      return []
    }
    return option.trains.map(train => {
      const seats = train.seats
      if (!seats || seats.length <= 0) {
        return '임의배정'
      }
      const carriageMap = {}
      seats.forEach(seat => {
        if (!carriageMap[seat.carriageNo]) {
          carriageMap[seat.carriageNo] = []
        }
        if (seat.seatDescription) {
          carriageMap[seat.carriageNo].push(seat.seatDescription)
        }
      })

      return Object.keys(carriageMap)
        .map(carriageNo => {
          const seatDescriptionList = [...new Set(carriageMap[carriageNo])]
          return `${+carriageNo}호차 ${seatDescriptionList.join(', ')}`
        })
        .join(', ')
    })
  }, [option])
}

export function usePassengerMemo(option: TrainOption) {
  const convert = ({ key, item }) =>
    key === 'adult'
      ? { key, price: item.adultFare, count: item.adults, goodsNo: item.goodsNo }
      : { key, price: item.childFare, count: item.child, goodsNo: item.goodsNo }

  return useMemo(() => {
    if (!option) {
      return []
    }

    return option.trains
      .map(({ goodsNo, adultFare, childFare }) => ({
        adultFare,
        childFare,
        goodsNo,
        adults: option.adults,
        child: option.child,
      }))
      .map(item =>
        [
          { key: 'adult', item },
          { key: 'child', item },
        ]
          .map(convert)
          .filter(({ count }) => count > 0)
      )
  }, [option])
}

export function useRewardMemo(pointReward: number, coinReward: number) {
  return useMemo(() => {
    const reward = [
      pointReward ? toPoint(pointReward, true) : '',
      coinReward ? toCoin(coinReward, true) : '',
    ]
      .filter(v => v)
      .join(' + ')
    return pointReward + coinReward > 0 ? `${reward} 적립예정` : ''
  }, [pointReward, coinReward])
}

export function useStatusText(option: TrainOption) {
  return useMemo(() => {
    if (!option) {
      return ''
    }

    if (!option.availability) {
      if (option.unavailableReason === 'STOP') {
        return '예약 마감'
      }
      if (option.unavailableReason === 'SOLD_OUT') {
        return '해당좌석 매진'
      }
      return '예약 불가'
    }
    if (option.outdated) {
      return '예약 불가'
    }
    return ''
  }, [option])
}

export function useBusStatusText(option: DomesticBusCartItemOption) {
  return useMemo(() => {
    if (!option) return null

    if (option.outdated) return '예약 불가'
    const text = busUnavailableReasonText(option.unavailableReason)
    if (text === '가격 변경') return '예약 불가'
    if (!isEmpty(text) || !isNil(text)) return text
    if (!option.availability) return '예약 불가'
    return null
  }, [option])
}

export function useTrainLogoMemo(stationTrainClassifyCode: string) {
  return useMemo(() => getTrainImage(stationTrainClassifyCode).h16, [stationTrainClassifyCode])
}
